import {
  type CreateAdminPropertyDto,
  type UpdateAdminPropertyDto,
  type V2AdminPropertiesPageDto,
  type V2AdminPropertiesSummaryDto,
  type V2AdminPropertyDto,
  v2AdminPropertiesControllerCreate,
  v2AdminPropertiesControllerDetail,
  v2AdminPropertiesControllerList,
  v2AdminPropertiesControllerLookup,
  v2AdminPropertiesControllerRemove,
  v2AdminPropertiesControllerSummary,
  v2AdminPropertiesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminPropertiesPageSchema,
  V2AdminPropertiesSummarySchema,
  V2AdminPropertyLookupSchema,
  V2AdminPropertySchema
} from '@idc-forms/schemas/api-v2/admin-properties'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/** Una opción del selector de inmuebles: el `prp_`, cómo se muestra y a qué proyecto pertenece. */
export type PropertyV2LookupOption = {
  id: string
  code: string
  name: string
  project: string
}

/**
 * Administración de inmuebles contra la API v2 (ADR 0019/0020). Consume el SDK
 * generado con el Bearer de la sesión staff v2, pagina por cursor y usa el contrato
 * de consulta rico. Identifica todo por id público (`prp_`); el proyecto viaja por
 * `prj_` y el subtipo por `typ_`, nunca como ObjectId. El IUSI viaja como Money (GTQ).
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer` con
 * bracket notation. El serializer por defecto del SDK usa `deepObject` de UN solo
 * nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type PropertiesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type PropertiesAdminV2Row = V2AdminPropertiesPageDto['data'][number]

function toListQuery(query: PropertiesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const propertiesAdminV2Service = {
  list: async (
    accessToken: string,
    query: PropertiesAdminV2Query
  ): Promise<V2AdminPropertiesPageDto> => {
    const result = await v2AdminPropertiesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminPropertiesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<PropertiesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminPropertiesSummaryDto> => {
    const result = await v2AdminPropertiesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminPropertiesSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminPropertyDto> => {
    const result = await v2AdminPropertiesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminPropertySchema, result.data)
  },

  create: async (
    accessToken: string,
    body: CreateAdminPropertyDto
  ): Promise<V2AdminPropertyDto> => {
    const result = await v2AdminPropertiesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminPropertyDto
  ): Promise<V2AdminPropertyDto> => {
    const result = await v2AdminPropertiesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  /**
   * Inmuebles para un selector, con sus dos filtros.
   *
   * `onlyAvailable` deja fuera lo que no se puede vender; `include` mantiene uno en la lista
   * pase lo que pase, que es lo que necesita el formulario al EDITAR — el inmueble de esa
   * negociación está tomado por ella misma, así que sin esto el select tendría un valor que no
   * figura entre sus opciones.
   */
  lookup: async (
    accessToken: string,
    query: { onlyAvailable?: boolean; include?: string } = {}
  ): Promise<PropertyV2LookupOption[]> => {
    const result = await v2AdminPropertiesControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query
    })
    return assertShape(V2AdminPropertyLookupSchema, result.data).data.map((property) => ({
      id: property.id,
      code: property.code,
      name: property.name,
      project: property.project
    }))
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminPropertiesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
